import { Alert, Button, CardDeck, Col, Form, Modal, ProgressBar } from 'react-bootstrap';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Row from 'react-bootstrap/Row';
import { toast } from 'react-toastify';
import { requestsActions, uploadActions } from '../../../actions';
import { InputField, UploadField } from '../../common/Forms';
import { validateFile } from '../../../util';
import { RequestOneFile } from '../../common/RequestFile/RequestOneFile';
import { BindUserForRequestFileModal } from '../../common/RequestFile/BindUserForRequestFileModal';
import { BindUserForFilesModal } from '../../common/RequestFile/BindUserForFilesModal';
import { ExpandableCard } from '../../common/ExpandableCard/ExpandableCard';

/**
 * Возвращает список данных о файлах заявки по заявке и информации о загружаемых файлах.
 *
 * @param {*} request объект заявки
 * @param {*} upload объект с информацией о загружаемых файлах относительно id заявок
 * @returns массив данных о файлах данной заявки
 */
function useUploadedFiles(request, upload = {}) {
    return useMemo(() => {
        const files = {};
        if (!request || !request.nomination.fileTypes) return files;
        request.nomination.fileTypes.forEach((fileType) => {
            files[fileType.id] = [];
        });
        if (!upload || !upload[request.id]) return files;
        Object.keys(upload[request.id]).forEach((fileId) => {
            const { fileTypeId } = upload[request.id][fileId];
            if (files[fileTypeId]) {
                files[fileTypeId].push(upload[request.id][fileId]);
            }
        });
        return files;
    }, [request, upload]);
}

function RequestFiles(props) {
    const { request, addFile, rmFile, addLink, readOnly, upload, getting, errors, clearErrors } = props;
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [fileToRemove, setFileToRemove] = useState(null);
    const [showNewVideoLink, setShowNewVideoLink] = useState();
    const [fileToBind, setFileToBind] = useState(null);
    const [filesToAdd, setFilesToAdd] = useState(null);

    useEffect(() => {
        if (!getting && !errors) setShowNewVideoLink(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getting]);

    const uploadingFiles = useUploadedFiles(request, upload);
    const { isNeedFiles, isNeedSpecifyUsers } = useMemo(() => {
        const isNeedFiles = {};
        const isNeedSpecifyUsers = {};
        if (!request || !request.nomination || !request.nomination.fileTypes)
            return { isNeedFiles, isNeedSpecifyUsers };
        request.nomination.fileTypes.forEach((fileType) => {
            isNeedFiles[fileType.id] =
                fileType.isRequired && (!request.files || !request.files.find((f) => f.fileType.id === fileType.id));
            isNeedSpecifyUsers[fileType.id] =
                !fileType.isGlobal &&
                request.files &&
                request.users &&
                !!request.users.find(
                    (u) =>
                        u.role === 'participant' &&
                        !request.files.find((f) => f.fileType.id === fileType.id && f.userId === u.user.id)
                );
        });
        return { isNeedFiles, isNeedSpecifyUsers };
    }, [request]);

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            addFile();
        },
        [addFile]
    );
    const handleAddFilesShow = useCallback(
        async (e, fileType) => {
            if (e.target.files) {
                const files = [];
                for (let i = 0; i < e.target.files.length; i++) {
                    const file = e.target.files[i];
                    const errorMes = await validateFile(file, request, fileType);
                    if (!errorMes) files.push({ file, fileType });
                    else toast.error(`Ошибка загрузки "${file.name}": ${errorMes}`);
                }
                if (files.length) {
                    if (fileType.isGlobal) {
                        for (let i = 0; i < files.length; i++) {
                            addFile({
                                requestId: request.id,
                                fileTypeId: fileType.id,
                                file: files[i].file,
                                userId: null,
                            });
                        }
                    } else {
                        setFilesToAdd(files);
                    }
                }
            }
            e.target.value = null;
        },
        [addFile, request]
    );
    const handleBindUsersClose = useCallback(() => {
        setFilesToAdd(null);
    }, []);

    const handleCloseConfirmModal = useCallback(() => {
        setFileToRemove(null);
        setShowConfirmModal(false);
    }, []);
    const handleDelFileClick = useCallback((file) => {
        setFileToRemove(file);
        setShowConfirmModal(true);
    }, []);

    const handleShowBindUserClick = useCallback((file) => {
        setFileToBind(file);
    }, []);
    const handleBindUserClose = useCallback(() => {
        setFileToBind(null);
    }, []);

    const handleConfirmDelFileClick = useCallback(() => {
        if (fileToRemove) rmFile({ requestId: request.id, fileId: fileToRemove.id });
        handleCloseConfirmModal();
    }, [handleCloseConfirmModal, request, rmFile, fileToRemove]);
    const handleAddVideoLink = useCallback(
        (e, field, value) => {
            if (getting || !showNewVideoLink) return;
            addLink({ requestId: +request.id, fileTypeId: +showNewVideoLink, link: value });
        },
        [getting, request, addLink, showNewVideoLink]
    );
    const handleCancelAddVideoLink = useCallback(() => {
        setShowNewVideoLink(null);
    }, []);
    //console.log('render files', request.files);
    return (
        <ExpandableCard title="Материалы" defaultExpanded={true}>
            {request.nomination.fileTypes &&
                request.nomination.fileTypes.map((fileType) => (
                    <div key={fileType.id} className="mb-3">
                        <h5 className="border-bottom">{fileType.name}</h5>
                        <div className="small mb-3" dangerouslySetInnerHTML={{ __html: fileType.description }} />
                        {isNeedFiles[fileType.id] && (
                            <Alert variant="danger">
                                Материалы данной категории обязательны. Пожалуйста, добавьте нужные файлы.
                            </Alert>
                        )}
                        {isNeedSpecifyUsers[fileType.id] && (
                            <Alert variant="danger">
                                Не для всех участников в заявке загружены соответствующие файлы. Пожалуйста, добавьте
                                нужные файлы или укажите какой файл какому участнику соответствует.
                            </Alert>
                        )}
                        <CardDeck>
                            {request.files &&
                                request.files
                                    .filter((f) => f.fileType.id === fileType.id)
                                    .map((file) => (
                                        <RequestOneFile
                                            key={file.id}
                                            file={file}
                                            request={request}
                                            readOnly={readOnly}
                                            getting={getting}
                                            onDeleteClick={handleDelFileClick}
                                            onBindUserClick={handleShowBindUserClick}
                                        />
                                    ))}
                        </CardDeck>
                        {uploadingFiles &&
                            uploadingFiles[fileType.id] &&
                            uploadingFiles[fileType.id].map((file) => (
                                <Row key={file.id}>
                                    <Col>
                                        <span>{file.name}</span>
                                        {file.errors ? (
                                            <div className="text-danger">Ошибка загрузки файла</div>
                                        ) : (
                                            <ProgressBar animated now={file.progress} />
                                        )}
                                    </Col>
                                </Row>
                            ))}
                        {!readOnly && (
                            <div className="mt-4 text-right">
                                {showNewVideoLink === fileType.id && (
                                    <InputField
                                        onCancel={handleCancelAddVideoLink}
                                        onSubmit={handleAddVideoLink}
                                        errors={errors}
                                        clearErrors={clearErrors}
                                        field={{
                                            id: 'link',
                                            name: 'Ссылка',
                                            inputType: 'url',
                                            required: true,
                                        }}
                                    />
                                )}
                                <Form noValidate onSubmit={handleSubmit}>
                                    {fileType.supportedVariants.split('|').includes('FILE') && (
                                        <UploadField fileType={fileType} onChange={handleAddFilesShow} />
                                    )}
                                    {fileType.supportedVariants.split('|').includes('VIDEO_LINK') &&
                                        showNewVideoLink !== fileType.id && (
                                            <Button
                                                variant="outline-primary"
                                                className="ml-2 px-4 position-relative"
                                                onClick={() => setShowNewVideoLink(fileType.id)}
                                            >
                                                Добавить ссылку
                                            </Button>
                                        )}
                                </Form>
                            </div>
                        )}
                    </div>
                ))}

            <Modal show={showConfirmModal} onHide={handleCloseConfirmModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Подтвердите действие</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Вы уверены что хотите удалить файл{' '}
                    <span className="text-break">{fileToRemove && `"${fileToRemove.filename}"`}</span>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseConfirmModal}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleConfirmDelFileClick}>
                        Уверен
                    </Button>
                </Modal.Footer>
            </Modal>

            <BindUserForRequestFileModal request={request} requestFile={fileToBind} onClose={handleBindUserClose} />
            <BindUserForFilesModal request={request} files={filesToAdd} onClose={handleBindUsersClose} />
        </ExpandableCard>
    );
}

function mapStateToProps(state) {
    const {
        requests: { requests, getting, errors },
        auth: { user },
        upload,
    } = state;
    return { requests, getting, errors, user, upload };
}

const actionsToProps = {
    clearErrors: requestsActions.clearErrors,
    addFile: uploadActions.addFile,
    rmFile: requestsActions.rmFile,
    addLink: requestsActions.addLink,
};

export default connect(mapStateToProps, actionsToProps)(RequestFiles);
